import React, { useEffect } from 'react';
import { useDateTimeSelection } from '../../../../hooks/useDateTimeSelection';
import DateTimeSelectionView from './DateTimeSelectionView';
import NavigationButtons from '../../shared/NavigationButtons';

/**
 * Componente container para a etapa de seleção de data, horário e profissional
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.salonId - ID do salão
 * @param {Object} props.salonConfig - Configurações do salão
 * @param {Function} props.onContinue - Função chamada ao continuar
 * @param {Function} props.onBack - Função chamada ao voltar
 * @param {boolean} props.isAdmin - Indica se está no modo admin
 * @param {Date} props.initialDate - Data inicial (opcional)
 * @param {string} props.initialTime - Horário inicial (opcional)
 * @param {boolean} props.allowManualTimeEntry - Permite entrada manual de horário (opcional)
 * @param {string} props.excludeAppointmentId - ID do agendamento a excluir da verificação (para remarcações)
 */
const DateTimeSelection = ({
  salonId,
  salonConfig = {},
  onContinue,
  onBack,
  isAdmin = false,
  initialDate = null,
  initialTime = null,
  allowManualTimeEntry = false,
  excludeAppointmentId = null
}) => {
  // Usar o hook personalizado para gerenciar a seleção de data/hora/profissional
  const {
    selectedDate,
    selectedTime,
    selectedProfessional,
    availableTimeSlots,
    detailedTimeSlots,
    availableProfessionals,
    isLoading,
    continueDisabled,
    handleDateChange,
    handleTimeSelect,
    handleProfessionalSelect,
    validateAndContinue
  } = useDateTimeSelection(salonId, {
    initialDate,
    initialTime,
    excludeAppointmentId
  });
  
  // Log para verificar se os parâmetros estão corretos
  useEffect(() => {
    if (excludeAppointmentId) {
      console.log(`[DateTimeSelection] Usando excludeAppointmentId=${excludeAppointmentId} para a seleção de horários`);
    }
    console.log(`[DateTimeSelection] TimeSlots disponíveis: ${availableTimeSlots?.length || 0}, Detalhados: ${detailedTimeSlots?.length || 0}`);
  }, [excludeAppointmentId, availableTimeSlots, detailedTimeSlots]);
  
  // Função para avançar para a próxima etapa
  const handleContinue = async () => {
    // Verificação adicional para garantir que o profissional não seja nulo
    if (!selectedProfessional) {
      console.warn('[DateTimeSelection] Tentativa de continuar sem selecionar um profissional');
      return;
    }
    
    console.log('[DateTimeSelection] Executando handleContinue com:', {
      selectedDate: selectedDate ? selectedDate.toISOString() : 'null',
      selectedTime,
      selectedProfessional: typeof selectedProfessional === 'object' ? selectedProfessional.id : selectedProfessional,
      excludeAppointmentId
    });
    
    const success = await validateAndContinue();
    console.log('[DateTimeSelection] validateAndContinue retornou:', success);
    
    if (success && onContinue) {
      console.log('[DateTimeSelection] Preparando dados para enviar ao onContinue do parent');
      
      // Preparar dados para passar ao componente pai
      const dataToSend = {
        date: selectedDate,
        time: selectedTime,
        professional: selectedProfessional
      };
      
      console.log('[DateTimeSelection] Chamando onContinue do parent com:', dataToSend);
      onContinue(dataToSend);
      console.log('[DateTimeSelection] onContinue do parent executado');
    }
  };
  
  // Usar detailedTimeSlots se disponível, senão usar availableTimeSlots
  const timeSlotsToShow = detailedTimeSlots?.length > 0 ? detailedTimeSlots : availableTimeSlots;
  
  return (
    <DateTimeSelectionView
      selectedDate={selectedDate}
      onDateChange={handleDateChange}
      availableTimeSlots={timeSlotsToShow}
      selectedTime={selectedTime}
      onTimeSelect={handleTimeSelect}
      availableProfessionals={availableProfessionals}
      selectedProfessional={selectedProfessional}
      onProfessionalSelect={handleProfessionalSelect}
      selectedServices={[]} // Obtido implicitamente no hook
      primaryColor={salonConfig?.primaryColor}
      isLoading={isLoading}
      onBack={onBack}
      onContinue={handleContinue}
      continueDisabled={continueDisabled}
      NavigationButtons={NavigationButtons}
      showAllSlots={isAdmin} // Permitir que admins vejam todos os slots
      allowManualTimeEntry={allowManualTimeEntry}
      isAdmin={isAdmin}
      salonConfig={salonConfig}
    />
  );
};

export default DateTimeSelection; 